import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTarih } from '../../lib/api';
import { IS_AD, Kart, SayfaBaslik, Sonuc, YayinDugmesi, YayinRozeti, sonYayinMetni, useSiteDurum, type IkiDil } from './ortak';
import { SonucRozeti, sure, type Gecmis } from './SiteYayinGecmisi';

type Ozet = {
  hero: { ust: IkiDil; kelimeler: IkiDil[]; dugmeler: { metin: IkiDil }[] }; duyuru: { acik: boolean; metin: IkiDil };
};
type Sayilar = { yazi: number; taslakYazi: number; gorsel: number; proje: number; telefon: string };

/** Sitenin küçük "ekran kartı": tarayıcı çerçevesi içinde taslaktaki hero'nun minyatürü. */
function EkranKarti({ o, adres }: { o: Ozet | null; adres: string | null }) {
  return (
    <div className="ccd-site-cerceve" aria-hidden="true">
      <div className="ccd-site-cerceve-ust">
        <span /><span /><span />
        <div className="ccd-site-cerceve-adres ccd-truncate">{(adres ?? 'site').replace(/^https?:\/\//, '').replace(/\/$/, '')}</div>
      </div>
      {o?.duyuru.acik && o.duyuru.metin.tr && <div className="ccd-site-cerceve-bant ccd-truncate">{o.duyuru.metin.tr}</div>}
      <div className="ccd-site-cerceve-govde">
        <div className="ccd-site-cerceve-ustyazi">{o?.hero.ust.tr}</div>
        <div className="ccd-site-cerceve-baslik">{o?.hero.kelimeler.map((k, i) => <span key={i}>{k.tr}</span>)}</div>
        <div className="ccd-site-cerceve-dugmeler">{o?.hero.dugmeler.map((d, i) => d.metin.tr && <span key={i}>{d.metin.tr}</span>)}</div>
      </div>
    </div>
  );
}

/** Site — genel bakış: ekran kartı, yayın durumu, kısa yollar ve son yayınlar. */
export default function SiteGenelBakis() {
  const { durum, yenile } = useSiteDurum();
  const [ozet, setOzet] = useState<Ozet | null>(null);
  const [sayi, setSayi] = useState<Sayilar | null>(null);
  const [gecmis, setGecmis] = useState<Gecmis | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);

  const yayinId = durum?.sonYayin?.id ?? 0, icerikVar = durum?.icerikVar ?? null;
  useEffect(() => {
    if (icerikVar === null) return;
    void api<{ veri: Ozet }>('/api/site/icerik/anasayfa').then((r) => setOzet(r.veri)).catch(() => undefined);
    void Promise.all([
      api<{ yazilar: { status: string }[] }>('/api/site/blog'), api<{ gorseller: unknown[] }>('/api/site/medya'),
      api<{ projeler: unknown[] }>('/api/site/projeler'), api<{ veri: { telefon: string } }>('/api/site/icerik/genel'),
    ]).then(([b, m, p, g]) => setSayi({
      yazi: b.yazilar.length, taslakYazi: b.yazilar.filter((y) => y.status !== 'YAYINDA').length, gorsel: m.gorseller.length, proje: p.projeler.length, telefon: g.veri.telefon,
    })).catch(() => undefined);
    void api<Gecmis>('/api/site/yayin').then(setGecmis).catch(() => undefined);
  }, [yayinId, icerikVar]);

  async function iceAktar() {
    setBilgi(null); setHata(null); setMesgul(true);
    try {
      const r = await api<{ genel: boolean; anasayfa: boolean; projeler: number; yazilar: number }>('/api/site/ice-aktar', { method: 'POST' });
      setBilgi(`İçe aktarıldı: ${[r.genel && 'ayarlar', r.anasayfa && 'ana sayfa', r.projeler && `${r.projeler} proje`, r.yazilar && `${r.yazilar} blog yazısı`].filter(Boolean).join(', ') || 'yeni bir şey yok'}.`);
      await yenile();
    } catch (e) { setHata(e instanceof Error ? e.message : 'İçe aktarılamadı'); }
    finally { setMesgul(false); }
  }

  const KISAYOL: [string, string, string][] = [
    ['/site/blog', 'Blog yazıları', sayi ? `${sayi.yazi} yazı${sayi.taslakYazi ? ` · ${sayi.taslakYazi} taslak` : ''}` : '…'],
    ['/site/projeler', 'Projeler', sayi ? `${sayi.proje} referans` : '…'],
    ['/site/gorseller', 'Görseller', sayi ? `${sayi.gorsel} dosya` : '…'],
    ['/site/ayarlar', 'Genel bilgiler', sayi ? sayi.telefon : '…'],
  ];
  const son = (gecmis?.gecmis ?? []).filter((k) => !k.kuru).slice(0, 5);

  return (
    <div className="ccd-stack">
      <SayfaBaslik baslik="Site" alt={durum?.siteAdresi ? durum.siteAdresi.replace(/^https?:\/\//, '') : undefined}>
        {durum?.siteAdresi && <a className="ccd-btn" href={durum.siteAdresi} target="_blank" rel="noreferrer">Siteyi aç</a>}
      </SayfaBaslik>
      <Sonuc bilgi={bilgi} hata={hata} />

      {durum && !durum.icerikVar && (
        <div className="ccd-card ccd-site-serit-kart">
          <div>
            <div className="ccd-site-kart-baslik">Sitenin bugünkü içeriği panelde yok</div>
            <div className="ccd-meta">İçe aktarınca ayarlar, ana sayfa, referanslar ve blog yazıları panele gelir.</div>
          </div>
          <button type="button" className="ccd-btn ccd-btn-primary" disabled={mesgul} onClick={() => void iceAktar()}>Sitedeki içeriği içe aktar</button>
        </div>
      )}

      <section className="ccd-card ccd-site-vitrin">
        <EkranKarti o={ozet} adres={durum?.siteAdresi ?? null} />
        <div className="ccd-site-vitrin-yan">
          <div className="ccd-stack-sm">
            <div className="ccd-eyebrow">Ana sayfa</div>
            <div className="ccd-site-vitrin-durum"><YayinRozeti durum={durum} /></div>
            <div className="ccd-meta">{durum ? sonYayinMetni(durum) : 'Yükleniyor…'}</div>
            {durum && !durum.hatKurulu && <div className="ccd-meta">Yayın hattı bu sunucuda kurulmamış; içerik düzenlenebilir.</div>}
          </div>
          <div className="ccd-site-vitrin-eylem">
            <Link className="ccd-btn ccd-btn-primary" to="/site/ozellestir" data-site-ozellestir>Siteyi özelleştir</Link>
            <YayinDugmesi durum={durum} yenile={() => void yenile()} birincil={false} />
          </div>
        </div>
      </section>

      <div className="ccd-site-kisayollar">
        {KISAYOL.map(([to, ad, alt]) => (
          <Link key={to} to={to} className="ccd-card ccd-site-kisayol">
            <span className="ccd-site-kisayol-ad">{ad}</span>
            <span className="ccd-meta ccd-truncate">{alt}</span>
            <span className="ccd-site-kisayol-ok" aria-hidden="true">›</span>
          </Link>
        ))}
      </div>

      <Kart baslik="Son yayınlar" eylem={<Link className="ccd-site-metin-dugme" to="/site/yayin-gecmisi">Tüm geçmiş</Link>} className="ccd-site-sonyayin">
        {!gecmis ? <div className="ccd-inline-note">Yükleniyor…</div> : son.length === 0 ? <div className="ccd-meta">Panelden henüz yayın yapılmadı.</div> : (
          <ul className="ccd-site-liste">
            {son.map((k) => (
              <li key={k.id}>
                <span className="ccd-site-liste-ana">
                  <span className="ccd-truncate">#{k.id} · {IS_AD[k.tur]}{k.degisiklikler.length ? ` · ${k.degisiklikler.length} değişiklik` : ''}</span>
                  <span className="ccd-meta ccd-truncate">{formatTarih(k.baslangic)}{k.kullanici ? ` · ${k.kullanici}` : ''} · {sure(k.sureMs)}</span>
                </span>
                <span className="ccd-td-nowrap"><SonucRozeti k={k} /></span>
              </li>
            ))}
          </ul>
        )}
      </Kart>
    </div>
  );
}
